import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Droplets, Heart, Zap, Leaf, LogOut, User, Moon, Crown, Flower2, Flame, CreditCard } from 'lucide-react';
import ReferralProgram from '../components/ReferralProgram';
import SkinCoachChat from '../components/SkinCoachChat';
import { useDashboardData } from '../hooks/useDashboardData';
import { logError } from '../lib/errorLogger';
import { toast } from 'sonner';
import DetoxScoreCard from '../components/DetoxScoreCard';
import StreakCard from '../components/StreakCard';
import ShareScoreCard from '../components/ShareScoreCard';
import DailyLogTab from '../components/DailyLogTab';
import PatternsSection from '../components/PatternsSection';
import RecommendationsSection from '../components/RecommendationsSection';
import SkinProgressSection from '../components/SkinProgressSection';
import SubscriptionSection from '../components/SubscriptionSection';
import OnboardingWizard from '../components/OnboardingWizard';
import GlowGarden from '../components/GlowGarden';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const VALID_TABS = ['log', 'patterns', 'recommendations', 'progress', 'garden', 'subscription'];

const QuickStats = ({ waterGlasses, bloatingLevel, acneSeverity, sleepHours, stressLevel, hasLog }) => (
  <Card className="card-elevated border-0" data-testid="quick-stats-card">
    <CardHeader><CardTitle className="text-[#1A1514]">Today's Log</CardTitle></CardHeader>
    <CardContent>
      {hasLog ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <StatTile icon={<Droplets className="w-6 h-6 text-[#FF6B4A] mb-2" />} value={waterGlasses} label="glasses of water" />
          <StatTile icon={<Heart className="w-6 h-6 text-[#A388EE] mb-2" />} value={`${bloatingLevel}/10`} label="bloating level" />
          <StatTile icon={<Zap className="w-6 h-6 text-[#4ADE80] mb-2" />} value={`${acneSeverity}/10`} label="acne severity" />
          <StatTile icon={<Moon className="w-6 h-6 text-[#FF6B4A] mb-2" />} value={`${sleepHours}h`} label="sleep last night" />
          <StatTile icon={<Flame className="w-6 h-6 text-[#A388EE] mb-2" />} value={`${stressLevel}/10`} label="stress level" />
        </div>
      ) : (
        <p className="text-[#8C7A76] text-center py-4">No log for today yet. Tap the Daily Log tab to get started.</p>
      )}
    </CardContent>
  </Card>
);

const StatTile = ({ icon, value, label }) => (
  <div className="p-4 bg-[#FFF2EC] rounded-xl">
    {icon}
    <p className="text-2xl font-bold text-[#1A1514]">{value}</p>
    <p className="text-sm text-[#8C7A76]">{label}</p>
  </div>
);

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    if (searchParams.get('quicklog')) return 'log';
    const t = searchParams.get('tab');
    return VALID_TABS.includes(t) ? t : 'log';
  });
  const [loading, setLoading] = useState(false);
  const [onboardingDone, setOnboardingDone] = useState(false);

  const {
    detoxScore, patterns, recommendations, selfies, logHistory, todayLog,
    saveLog, uploadSelfie, LOG_DEFAULTS,
  } = useDashboardData();

  // Deep-link from push notifications / app shortcuts (e.g. ?quicklog=1, ?tab=progress)
  useEffect(() => {
    if (searchParams.get('quicklog')) { setActiveTab('log'); return; }
    const t = searchParams.get('tab');
    if (VALID_TABS.includes(t)) setActiveTab(t);
  }, [searchParams]);

  // App-icon badge: nudge when today isn't logged yet, clear once logged
  useEffect(() => {
    if (!('setAppBadge' in navigator)) return;
    if (todayLog) {
      navigator.clearAppBadge?.().catch(() => {});
    } else {
      navigator.setAppBadge?.(1).catch(() => {});
    }
  }, [todayLog]);

  // Show onboarding if user has no logs and hasn't completed it this session
  const isNewUser = !todayLog && logHistory.length === 0 && !onboardingDone;

  const handleOnboardingComplete = useCallback(async (payload) => {
    try {
      // Save cycle tracking preference (non-blocking — don't let this fail the log)
      if (payload.track_cycle !== undefined) {
        try {
          await axios.patch(`${API}/user/preferences`, { track_cycle: payload.track_cycle }, { withCredentials: true });
        } catch (prefErr) {
          logError('Onboarding', 'Failed to save preferences (non-critical)', { error: prefErr?.message });
        }
      }
      // Strip track_cycle before sending to logs endpoint
      const { track_cycle, ...logPayload } = payload;
      await saveLog(logPayload);
      toast.success("Welcome! Your first log is saved and your detox score is ready.");
    } catch (err) {
      logError('Onboarding', 'Failed to save first log', { error: err?.message, response: err?.response?.data });
      toast.error('Could not save your log. You can log again from the dashboard.');
    }
    // Small delay then show dashboard
    setTimeout(() => setOnboardingDone(true), 1500);
  }, [saveLog]);

  const handleSaveLog = useCallback(async (payload) => {
    setLoading(true);
    try {
      await saveLog(payload);
      toast.success('Daily log saved! Your detox score has been updated.');
      // Auto-plant seed in garden
      try {
        const plantRes = await axios.post(`${API}/garden/plant`, {}, { withCredentials: true });
        if (plantRes.data.status === 'planted') {
          const mult = plantRes.data.streak_multiplier > 1 ? ` (${plantRes.data.streak_multiplier}x streak!)` : '';
          toast.success(`${plantRes.data.plant.name} planted in your garden! +${plantRes.data.xp_earned} XP${mult}`);
        }
      } catch { /* garden plant is optional */ }
    } catch (err) {
      logError('Dashboard', 'Failed to save daily log', { error: err?.message });
      toast.error('Failed to save log. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [saveLog]);

  const handleSelfieUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = async () => {
      setLoading(true);
      try {
        await uploadSelfie(reader.result.split(',')[1]);
        toast.success('Selfie uploaded! AI analysis in progress...');
      } catch (err) {
        logError('Dashboard', 'Failed to upload selfie', { error: err?.message });
        toast.error('Upload failed. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    reader.readAsDataURL(file);
  }, [uploadSelfie]);

  const handleSelfieCapture = useCallback(async (base64) => {
    setLoading(true);
    try {
      await uploadSelfie(base64);
      toast.success('Scan complete! Your AI skin report is ready.');
    } catch (err) {
      logError('Dashboard', 'Failed to analyze scan', { error: err?.message });
      toast.error('Analysis failed. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [uploadSelfie]);

  const water = todayLog?.water_glasses ?? 0;
  const bloat = todayLog?.bloating_level ?? 0;
  const acne = todayLog?.acne_severity ?? 0;
  const sleep = todayLog?.sleep_hours ?? 0;
  const stress = todayLog?.stress_level ?? 0;

  const streakForShare = useMemo(() => ({ current_streak: logHistory.length }), [logHistory.length]);

  // Show onboarding wizard for new users
  if (isNewUser) {
    return <OnboardingWizard userName={user?.name} onComplete={handleOnboardingComplete} />;
  }

  return (
    <div className="min-h-screen bg-[#FFF2EC]" data-testid="dashboard">
      <header className="glass-header sticky top-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Leaf className="w-8 h-8 text-[#FF6B4A]" />
            <span className="font-heading text-xl font-bold text-[#1A1514]">InnerGlow</span>
          </div>
          <div className="flex items-center gap-4">
            {user?.subscription === 'premium' && (
              <span className="hidden sm:flex items-center gap-1 text-xs font-semibold text-[#4ADE80] bg-[#4ADE80]/15 px-3 py-1 rounded-full">
                <Crown className="w-3.5 h-3.5" /> Premium
              </span>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="account-dropdown-trigger"
                  className="w-9 h-9 rounded-full bg-[#FF6B4A] text-white font-bold flex items-center justify-center overflow-hidden ring-2 ring-white shadow-md hover:scale-105 transition-transform">
                  {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (user?.name?.[0]?.toUpperCase() || <User className="w-4 h-4" />)}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white border border-[#E5D0C9] shadow-xl rounded-xl" data-testid="user-menu-dropdown">
                <DropdownMenuLabel>
                  <p className="text-sm font-bold text-[#1A1514] truncate">{user?.name}</p>
                  <p className="text-xs text-[#8C7A76] font-normal truncate">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-[#F3E6E0]" />
                <DropdownMenuItem onClick={() => navigate('/account')} className="cursor-pointer" data-testid="menu-my-account">
                  <User className="w-4 h-4 mr-2 text-[#FF6B4A]" /> My Account
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/account#billing')} className="cursor-pointer" data-testid="menu-billing">
                  <CreditCard className="w-4 h-4 mr-2 text-[#A388EE]" /> Billing & Subscription
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[#F3E6E0]" />
                <DropdownMenuItem onClick={logout} className="cursor-pointer text-[#FF3B30] focus:text-[#FF3B30]" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
          <DetoxScoreCard detoxScore={detoxScore} trackCycle={user?.track_cycle !== false} />
          <div className="lg:col-span-8 space-y-6">
            <QuickStats waterGlasses={water} bloatingLevel={bloat} acneSeverity={acne} sleepHours={sleep} stressLevel={stress} hasLog={!!todayLog} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <StreakCard />
              <ShareScoreCard detoxScore={detoxScore} userName={user?.name} streak={streakForShare} />
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white p-1 rounded-full shadow-sm flex-wrap">
            <TabsTrigger value="log" className="rounded-full data-[state=active]:bg-[#FF6B4A] data-[state=active]:text-white px-6" data-testid="tab-log">Daily Log</TabsTrigger>
            <TabsTrigger value="patterns" className="rounded-full data-[state=active]:bg-[#FF6B4A] data-[state=active]:text-white px-6" data-testid="tab-patterns">Patterns</TabsTrigger>
            <TabsTrigger value="recommendations" className="rounded-full data-[state=active]:bg-[#FF6B4A] data-[state=active]:text-white px-6" data-testid="tab-recommendations">Recommendations</TabsTrigger>
            <TabsTrigger value="progress" className="rounded-full data-[state=active]:bg-[#FF6B4A] data-[state=active]:text-white px-6" data-testid="tab-progress">Skin Progress</TabsTrigger>
            <TabsTrigger value="garden" className="rounded-full data-[state=active]:bg-[#4ADE80] data-[state=active]:text-white px-6" data-testid="tab-garden">
              <Flower2 className="w-4 h-4 mr-1" /> Garden
            </TabsTrigger>
            <TabsTrigger value="subscription" className="rounded-full data-[state=active]:bg-[#FF6B4A] data-[state=active]:text-white px-6" data-testid="tab-subscription">
              <Crown className="w-4 h-4 mr-1" /> Premium
            </TabsTrigger>
          </TabsList>

          <TabsContent value="log" className="space-y-6">
            <DailyLogTab todayLog={todayLog} onSave={handleSaveLog} loading={loading} defaults={LOG_DEFAULTS} trackCycle={user?.track_cycle !== false} />
          </TabsContent>
          <TabsContent value="patterns"><PatternsSection patterns={patterns} logHistory={logHistory} subscription={user?.subscription || 'free'} onUpgrade={() => setActiveTab('subscription')} /></TabsContent>
          <TabsContent value="recommendations"><RecommendationsSection recommendations={recommendations} subscription={user?.subscription || 'free'} onUpgrade={() => setActiveTab('subscription')} /></TabsContent>
          <TabsContent value="progress"><SkinProgressSection selfies={selfies} onSelfieUpload={handleSelfieUpload} onSelfieCapture={handleSelfieCapture} loading={loading} subscription={user?.subscription || 'free'} onUpgrade={() => setActiveTab('subscription')} /></TabsContent>
          <TabsContent value="garden"><GlowGarden /></TabsContent>
          <TabsContent value="subscription" className="space-y-6">
            <SubscriptionSection subscription={user?.subscription || 'free'} />
            <ReferralProgram />
          </TabsContent>
        </Tabs>
      </main>
      <SkinCoachChat />
    </div>
  );
};

export default Dashboard;
